/**
 * Marketing attribution capture — stores the landing URL the visitor arrived on from an EXTERNAL
 * marketing source (ad, email, referring site), so it can be persisted against the business at
 * registration.
 *
 * Session-scoped and opaque-keyed. Captured once, the first time the user lands on the portal
 * carrying genuine campaign parameters. The portal must NEVER capture itself as a referral — this
 * is enforced by requiring a recognized campaign parameter (see ATTRIBUTION_PARAMS):
 *   • analytics-only params that Google Analytics / GTM append automatically on ordinary (even
 *     internal) navigations — the cross-domain linker `_gl` and the Ads auto-tag `_gcl_au` — do
 *     NOT count as attribution. (A prod business was wrongly captured with
 *     marketingReferral="https://portal-eu.firstorion.com/?_gl=..." — the portal's own home URL.)
 *   • a plain visit, an internal navigation, or the OIDC auth callback carries no campaign param,
 *     so nothing is stored.
 * No further validation — the attribution value is free text; the backend caps it at 1024 chars.
 */
export const MARKETING_REFERRAL_STORAGE_KEY = 'icip_mkt_ref';

const MAX_LEN = 1024;

/**
 * Query parameters that constitute genuine marketing attribution. Only when the landing URL
 * carries at least one of these (with a non-empty value) do we record a referral. This EXCLUDES
 * analytics-only params (`_gl`, `_gcl_au`) and auth params (`code`, `state`).
 */
const ATTRIBUTION_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid',
  'promo', 'ref', 'referrer',
];

/** True only when the query string carries at least one recognized attribution param. */
function hasAttributionParams(search: string): boolean {
  if (!search || search.length <= 1) return false;
  try {
    const params = new URLSearchParams(search);
    return ATTRIBUTION_PARAMS.some((p) => {
      const v = params.get(p);
      return v != null && v.trim() !== '';
    });
  } catch {
    return false;
  }
}

/**
 * Captures the landing URL the first time the user arrives from an external marketing source.
 * Runs at app bootstrap (so it fires on the genuine first landing). Writes once per session —
 * the first qualifying landing wins — and never records the portal referring itself.
 */
export function captureMarketingReferral(): void {
  try {
    // First qualifying landing wins — don't overwrite an already-captured referral.
    if (sessionStorage.getItem(MARKETING_REFERRAL_STORAGE_KEY)) return;

    // Never capture the Cognito OIDC callback (?code=...&state=...). It is a full page reload of
    // our own URL — a self-referral, not marketing attribution.
    if (window.location.pathname.startsWith('/auth/callback')) return;

    // Capture ONLY when the landing URL carries genuine campaign params. This is what stops the
    // portal capturing itself: a plain visit, an internal navigation, or a URL carrying only
    // analytics params (_gl, _gcl_au) that GA/GTM append automatically has no campaign param, so
    // nothing is stored. Only a real external marketing link (UTM/gclid/promo/etc.) qualifies.
    if (!hasAttributionParams(window.location.search)) return;

    const href = window.location.href;
    const value = href.length > MAX_LEN ? href.substring(0, MAX_LEN) : href;
    sessionStorage.setItem(MARKETING_REFERRAL_STORAGE_KEY, value);
  } catch {
    /* no-op — non-browser context or blocked storage */
  }
}

/** Returns the stored marketing referral URL, or null if none / storage unavailable. */
export function readMarketingReferral(): string | null {
  try {
    const value = sessionStorage.getItem(MARKETING_REFERRAL_STORAGE_KEY);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

/** Removes the stored marketing referral (e.g. once consumed at registration). */
export function clearMarketingReferral(): void {
  try {
    sessionStorage.removeItem(MARKETING_REFERRAL_STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
